import isUndefined from "lodash/isUndefined";
import pick from "lodash/pick";
import defaults from "lodash/defaults";
import assert from "assert";
import deepFreeze from "deep-freeze";
import Device from "utils/Device";
import { notifyExternalOfAnalyticsTracking } from "utils/externalMessaging";
import { throttle } from "core-decorators";
import omit from "lodash/omit";
import send from "./utils/trackerSend";
import AnalyticsTrackingTypes from "./types/AnalyticsTrackingTypes";
import * as UIInteractedItemType from "./types/UIInteractedItemType";
import * as InteractionType from "./types/InteractionType";

export default class Tracker {
  static persistentData = {
    storyAuthor: "",
    storyId: -1,
    storyTitle: "",
    verseBrowser: Device.browser,
    viewer: "",
  };

  static isSingleVideoPlayer = false;

  static eventList = [];

  static setViewer(id) {
    Tracker.persistentData.viewer = id;
  }

  static setStoryData({ id: storyId, title: storyTitle, author: storyAuthor }) {
    if (DEVELOPMENT) {
      if (isUndefined(storyAuthor)) {
        storyAuthor = "unknown";
      }
    }

    Tracker.persistentData = {
      ...Tracker.persistentData,
      storyAuthor,
      storyId,
      storyTitle,
    };
  }

  static setPersistentData(extraData) {
    Tracker.persistentData = {
      ...Tracker.persistentData,
      ...extraData,
    };
  }

  static setIsSingleVideoPlayer(value) {
    Tracker.isSingleVideoPlayer = value;
  }

  static get lastEvent() {
    if (this.eventList.length) {
      return this.eventList[0];
    }

    return undefined;
  }

  static get slideViewedBecauseOfRecentTimelineClick() {
    const eventsExcludingMediaProgress = this.eventList.filter(
      (event) => event.event !== AnalyticsTrackingTypes.mediaProgress,
    );

    // We should have at least 2 events: the slide view event and
    // whatever happened before it. If we don't have at least 2 events,
    // then we can guarantee that the slide vew was not because of a
    // timeline click.
    if (eventsExcludingMediaProgress.length < 2) {
      return false;
    }

    const mostRecentEvent = eventsExcludingMediaProgress[0];
    if (mostRecentEvent.event !== AnalyticsTrackingTypes.slideViewed) {
      return false;
    }

    const eventBeforeSlideView = eventsExcludingMediaProgress[1];
    const targetEventType = AnalyticsTrackingTypes.uiInteracted;
    const targetUIInteractedItemType =
      UIInteractedItemType.TIMELINE_SLIDE_CLICK;

    // Double check that the targets are defined. This will be handled
    // automatically when `babel-plugin-typecheck` handles variables;
    // but at the time authoring it did not.
    //
    // https://github.com/codemix/babel-plugin-typecheck/issues/3
    assert(targetEventType);
    assert(targetUIInteractedItemType);

    // Now we know if the slide was viewed because of a timeline click,
    // but not if it was recent. If the slide was viewed for any other
    // reason than a timeline click, then return `false` because we
    // don't care *when* it happened.
    const slideViewedBecauseOfTimelineClick =
      eventBeforeSlideView.event === targetEventType &&
      eventBeforeSlideView.item === targetUIInteractedItemType;
    if (!slideViewedBecauseOfTimelineClick) {
      return false;
    }

    const elapsedSinceMs = Date.now() - eventBeforeSlideView.timestamp;

    // Return if the time elapsed since the timeline click event was
    // less than 2 seconds.
    return elapsedSinceMs < 2000;
  }

  // Return `true` if the last event logged was active. Return `false` if
  // the last event logged was passive, the previously logged event did
  // not define the interaction type, or there are no previously logged
  // events.
  static get lastEventWasActive() {
    const lastEvent = this.lastEvent;

    if (lastEvent) {
      return lastEvent.interactionType === InteractionType.ACTIVE;
    }

    return false;
  }

  static sendLinkOffered(data) {
    this.send(
      {
        ...data,
        event: AnalyticsTrackingTypes.linkOffered,
        interactionType: InteractionType.PASSIVE,
      },
      {
        onlySendLocally: true,
      },
    );
  }

  static sendHotspotLinkConverted(data) {
    this.send({
      ...data,
      event: AnalyticsTrackingTypes.linkConverted,
      interactionType: InteractionType.ACTIVE,
    });
  }

  static sendCuepointLinkConverted(data) {
    this.send({
      ...data,
      event: AnalyticsTrackingTypes.linkConverted,
      interactionType: InteractionType.ACTIVE,
    });
  }

  static sendUIInteraction(data, options) {
    const mutatedData = {
      ...data,
      event: AnalyticsTrackingTypes.uiInteracted,
      interactionType:
        options && options.isPassive
          ? InteractionType.PASSIVE
          : InteractionType.ACTIVE,
    };

    this.send(mutatedData, options);
  }

  static sendPassiveUIInteraction(data, options) {
    const mutatedData = {
      ...data,
      event: AnalyticsTrackingTypes.uiInteracted,
      interactionType: InteractionType.PASSIVE,
    };

    this.send(mutatedData, options);
  }

  static sendQuartile(data) {
    this.send(
      {
        ...data,
        event: AnalyticsTrackingTypes.quartile,
        interactionType: InteractionType.PASSIVE,
      },
      {
        onlySendLocally: false,
      },
    );
  }

  @throttle(5000)
  static sendMediaProgress(data) {
    this.send(
      {
        ...data,
        event: AnalyticsTrackingTypes.mediaProgress,
        interactionType: InteractionType.PASSIVE,
      },
      {
        onlySendLocally: true,
      },
    );
  }

  static send(data, options = {}) {
    // Set defaults.
    defaults(options, {
      determineInteractionType: false,
      onlySendLocally: false,
    });

    const sendOptions = pick(options, ["onlySendLocally"]);

    // Create new `parsedData` object that includes `data` and anything
    // stored in `this.persistentData`.
    let parsedData = {
      ...data,
      ...this.persistentData,
      timestamp: Date.now(),
    };

    if (options.determineInteractionType) {
      // Attempt to automatically determine `interactionType`.
      parsedData.interactionType = this.lastEventWasActive
        ? InteractionType.ACTIVE
        : InteractionType.PASSIVE;
    }

    if (Tracker.isSingleVideoPlayer) {
      parsedData = omit(parsedData, [
        "chapterId",
        "storyId",
        "chapterTitle",
        "storyTitle",
        "chapterType",
      ]);
    }

    window.eventList = Tracker.eventList = Object.freeze(
      [deepFreeze(parsedData), ...Tracker.eventList].slice(0, 10),
    );
    // emit tracking data in message and then send
    notifyExternalOfAnalyticsTracking(parsedData);
    send(parsedData, sendOptions);
  }
}
